import axios from 'utils/axios';
import { useState, useCallback } from 'react';

export interface UseReportsState {
    getUserOptionsInProgress: boolean
    getProgramQuestionOptionsInProgress: boolean
    getAdminRouteReportInProgress: boolean
    getAdminProfileReportInProgress: boolean
    getDonorRouteReportInProgress: boolean
    getMultiOrganizationReportInProgress: boolean
    getDonorTaxDeductionReportInProgress: boolean
    getDonorTaxDeductionRecipientOptionsInProgress: boolean
    report: any
    status: any
    programQuestions: any[]
    users: any[]
    taxDeductionRecipients: any[]
}

const useReports = () => {
    const [state, setState] = useState<UseReportsState>({
        getUserOptionsInProgress: false,
        getProgramQuestionOptionsInProgress: false,
        getAdminRouteReportInProgress: false,
        getAdminProfileReportInProgress: false,
        getDonorRouteReportInProgress: false,
        getMultiOrganizationReportInProgress: false,
        getDonorTaxDeductionReportInProgress: false,
        getDonorTaxDeductionRecipientOptionsInProgress: false,
        report: null,
        status: null,
        programQuestions: null,
        users: null,
        taxDeductionRecipients: null
    });

    // Get the user profiles report as an Admin
    const getAdminProfileReport = useCallback(async (params: any,) => {
        setState(s => ({ ...s, getAdminProfileReportInProgress: true }))

        const response = await axios
            .get<any>('berryAPI/report/profile', {params: params, responseType: 'blob'})
            .catch((error) => {
                console.log(error);
                setState(s => ({ ...s, getAdminProfileReportInProgress: false }));
                throw error;
            })

        setState(s => ({
            ...s,
            getAdminProfileReportInProgress: false,
            report: response.data,
            status: response.status
        }));

        return response.data;
    }, [setState]);

    // Get the route report user options
    const getUserOptions = useCallback(async (params: any) => {
        setState(s => ({ ...s, getUserOptionsInProgress: true }))

        const response = await axios
            .get<any>(`/berryAPI/report/routeReportUserOptions`, {params: params})
            .catch((error) => {
                console.log(error);
                setState(s => ({ ...s, getUserOptionsInProgress: false }));
                throw error;
            })

        setState(s => ({
            ...s,
            getUserOptionsInProgress: false,
            users: response.data.users,
            status: response.status
        }));

        return response.data.users;
    }, [setState]);

    // Get the route report program question options
    const getProgramQuestionOptions = useCallback(async (params: any) => {
        setState(s => ({ ...s, getProgramQuestionOptionsInProgress: true }))

        const response = await axios
            .get<any>(`/berryAPI/report/routeReportColumnOptions`, {params: params })
            .catch((error) => {
                console.log(error);
                setState(s => ({ ...s, getProgramQuestionOptionsInProgress: false }));
                throw error;
            })

        setState(s => ({
            ...s,
            getProgramQuestionOptionsInProgress: false,
            programQuestions: response.data.programQuestions,
            status: response.status
        }));

        return response.data.programQuestions;
    }, [setState]);

    // Get the route report as an Admin
    const getAdminRouteReport = useCallback(async (data: any) => {
        setState(s => ({ ...s, getAdminRouteReportInProgress: true }))

        const response = await axios
            .post(`/berryAPI/report/routeReportGenerate`, data, {responseType: 'blob' })
            .catch((error) => {
                console.log(error);
                setState(s => ({ ...s, getAdminRouteReportInProgress: false }));
                throw error;
            })

        setState(s => ({
            ...s,
            getAdminRouteReportInProgress: false,
            report: response.data,
            status: response.status
        }));

        return response.data;
    }, [setState]);

    // Get the route report as a Food Donor
    const getDonorRouteReport = useCallback(async (data: any) => {
        setState(s => ({ ...s, getDonorRouteReportInProgress: true }))

        const response = await axios
            .post('/berryAPI/donor/generateRouteReport', data, { responseType: 'blob' })
            .catch((error) => {
                console.log(error);
                setState(s => ({ ...s, getDonorRouteReportInProgress: false }));
                throw error;
            })

        setState(s => ({
            ...s,
            getDonorRouteReportInProgress: false,
            report: response.data,
            status: response.status
        }));

        return response.data;
    }, [setState]);

    // Get a multi-organization report as a superuser or super admin
    const getMultiOrganizationReport = useCallback(async (startDate: any, endDate: any, superuser: boolean = false) => {
        setState(s => ({ ...s, getMultiOrganizationReportInProgress: true }))

        const url: string = window.location.protocol + "//" + window.location.hostname +
            (superuser ? `/berryAPI/superuser/report?` : `/berryAPI/superadmin/report?`) +
            'startDate=' + startDate + '&endDate=' + endDate;

        const response = await axios
            .get(url, { responseType: 'blob' })
            .catch((error) => {
                console.log(error);
                setState(s => ({ ...s, getMultiOrganizationReportInProgress: false }));
                throw error;
            })

        setState(s => ({
            ...s,
            getMultiOrganizationReportInProgress: false,
            report: response.data,
            status: response.status
        }));

        return response.data;
    }, [setState]);

    // Get eligible recipients for donor tax deduction reports
    const getDonorTaxDeductionRecipientOptions = useCallback(async () => {
        setState((s) => ({ ...s, getDonorTaxDeductionRecipientOptionsInProgress: true }));

        const response = await axios
            .get<any>('/berryAPI/donor/taxDeductionRecipientOptions')
            .catch((error) => {
                console.log(error);
                setState((s) => ({ ...s, getDonorTaxDeductionRecipientOptionsInProgress: false }));
                throw error;
            });

        setState((s) => ({
            ...s,
            getDonorTaxDeductionRecipientOptionsInProgress: false,
            taxDeductionRecipients: response.data.users,
            status: response.status
        }));

        return response.data.users;
    }, [setState]);

    // Get the tax deduction report as a Food Donor
    const getDonorTaxDeductionReport = useCallback(async (data: any) => {
        setState((s) => ({ ...s, getDonorTaxDeductionReportInProgress: true }));

        const response = await axios
            .post('/berryAPI/donor/generateTaxDeductionReport', data, { responseType: 'blob' })
            .catch((error) => {
                console.log(error);
                setState((s) => ({ ...s, getDonorTaxDeductionReportInProgress: false }));
                throw error;
            });

        setState((s) => ({
            ...s,
            getDonorTaxDeductionReportInProgress: false,
            report: response.data,
            status: response.status
        }));

        return response.data;
    }, [setState]);

    return {
        ...state,
        getAdminProfileReport,
        getUserOptions,
        getProgramQuestionOptions,
        getAdminRouteReport,
        getDonorRouteReport,
        getMultiOrganizationReport,
        getDonorTaxDeductionRecipientOptions,
        getDonorTaxDeductionReport
    };
};

export default useReports;
